import * as UserActions from '@/app/auth/store/actions/user.actions';
import { uploadFile } from '@/app/services/imageUpload/imageUpload';
import { getSchoolProfile, updatesettings } from '@/app/services/settings/settings';
import * as Actions from '@/app/store/actions';
import { isImageFile, getImageUrl } from '@/utils/utils';
import { Avatar, CircularProgress } from '@material-ui/core';
import { isPossiblePhoneNumber } from 'libphonenumber-js/min';
import Pusher from 'pusher-js';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './Settings.css';
import GradientButton from '@shared/components/GradientButton';
import { Controller, useForm } from 'react-hook-form';
import StyledInput from '@shared/components/StyledInput';
import StyledSelect from '@shared/components/StyledSelect';
import StyledSearchSelect from '@shared/components/StyledSearchSelect';
import { getSearchableCityList } from '@/app/services/schools/schools';
import tz_lookup from 'tz-lookup';
import PageHeader from '@shared/components/PageHeader/PageHeader';
import { states } from '@shared/constants/states';
import { checkoutTimes } from '../../superadmin/schools/checkoutTimes';

const Settings = () => {
	const dispatch = useDispatch();
	const inputRef = useRef(null);
	const previousStateId = useRef(null);
	const [selectedFile, setSelectedFile] = useState(null);
	const [preview, setPreview] = useState(null);
	const [user, setUser] = useState({});
	const mainUser = useSelector(({ auth }) => auth.user);
	const [isEditing, setIsEditing] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [cities, setCities] = useState([]);
	const [citiesLoading, setCitiesLoading] = useState(false);
	const [latLong, setLatLong] = useState({ lat: '', long: '' });
	const [searchCityQuery, setSearchCityQuery] = useState('');
	const [defaultCity, setDefaultCity] = useState({});
	const [cityTimezone, setCityTimeZone] = useState('');
	let pusher;
	let channel;

	const {
		control,
		handleSubmit,
		reset,
		setError,
		watch,
		setValue,
		formState: { errors },
	} = useForm({
		defaultValues: {
			school_name: '',
			address: '',
			address2: '',
			school_phone: '',
			website: '',
			country_code: 'US',
			state_id: '',
			city: '',
			street: '',
			zip_code: '',
			timezone_checkin: '',
			timezone: '',
			status: 1,
			checkout_time: '',
		},
	});

	const selectedStateId = watch('state_id');

	useEffect(() => {
		let isMounted = true;
		setCitiesLoading(true);
		const timeout = setTimeout(() => {
			if (!isMounted) return;
			setCities([]);
			if (!searchCityQuery && !selectedStateId) {
				setCitiesLoading(false);
				setCities([]);
			} else {
				getSearchableCityList(selectedStateId, searchCityQuery, 1)
					.then((res) => {
						if (!isMounted) return;
						setCities(res.data.data);
					})
					.catch(() => {
						if (!isMounted) return;
						dispatch(
							Actions.showMessage({
								message: 'Failed to get cities.',
								autoHideDuration: 1500,
								variant: 'error',
							})
						);
					})
					.finally(() => {
						if (!isMounted) return;
						setCitiesLoading(false);
					});
			}
		}, 1000);
		return () => {
			isMounted = false;
			clearTimeout(timeout);
		};
	}, [selectedStateId, searchCityQuery]);

	useEffect(() => {
		if (selectedStateId && previousStateId.current && selectedStateId !== previousStateId.current) {
			setValue('city', '');
			setDefaultCity({});
			setLatLong({ lat: '', long: '' });
			setCityTimeZone('');
		}
		previousStateId.current = selectedStateId;
	}, [selectedStateId]);

	useEffect(() => {
		if (latLong.lat !== '' && latLong.long !== '') {
			const timezone = tz_lookup(latLong.lat, latLong.long);
			setCityTimeZone(timezone);
		}
	}, [latLong]);

	const GetSchoolSettings = () => {
		getSchoolProfile()
			.then((res) => {
				setPreview(res?.data?.logo);
				setUser(res.data);
				reset({
					school_name: res.data.name,
					address: res.data.address ?? '',
					address2: res.data.address2 ?? '',
					school_phone: res.data.phone ?? '',
					website: res.data.website ?? '',
					country_code: res.data.country_code,
					state_id: res.data.state_id,
					city: res.data.city ?? '',
					street: res.data.street ?? '',
					zip_code: res.data.zip_code ?? '',
					timezone_checkin: res.data.timezone_checkin ?? '',
					timezone: res.data.timezone ?? '',
					status: res.data.status,
					checkout_time: res.data.checkout_time || { label: '7:00 PM', value: '19:00:00' },
				});
				setDefaultCity({ name: res.data.city });
				const updatedAdmin = res.data.admins.filter((admin) => admin.id === mainUser.data.id)[0];
				if (!updatedAdmin) return;
				const updatedUser = {
					...mainUser,
					data: {
						...mainUser.data,
						displayName: `${updatedAdmin.first_name} ${updatedAdmin.last_name}`,
					},
					school: {
						...res.data,
					},
					doNotRedirect: 1,
				};
				dispatch(UserActions.setUserData(updatedUser));
			})
			.catch((error) => {
				console.error('data is invalid', error);
				dispatch(
					Actions.showMessage({
						message: 'Failed to get school settings',
						autoHideDuration: 1500,
						variant: 'error',
					})
				);
			});
	};

	useEffect(() => {
		pusher = new Pusher(import.meta.env.VITE_PUSHER_CHANNEL_ID, {
			cluster: import.meta.env.VITE_PUSHER_CLUSTER_ID,
		});
		channel = pusher.subscribe(`school_app_${mainUser.school?.id || mainUser.data.school.id}`);
		channel.bind('school_update', () => {
			GetSchoolSettings();
		});
		channel.bind('school_disabled', () => {
			GetSchoolSettings();
		});
		GetSchoolSettings();
		return () => {
			pusher.disconnect();
		};
	}, []);

	const handleCancel = () => {
		setIsEditing(false);
		reset({
			school_name: user.name,
			address: user.address ?? '',
			address2: user.address2 ?? '',
			school_phone: user.phone ?? '',
			website: user.website ?? '',
			country_code: user.country_code,
			state_id: user.state_id,
			city: user.city ?? '',
			street: user.street ?? '',
			zip_code: user.zip_code ?? '',
			timezone_checkin: user.timezone_checkin ?? '',
			timezone: user.timezone ?? '',
			status: user.status,
			checkout_time: user.checkout_time || { label: '7:00 PM', value: '19:00:00' },
		});
		setSelectedFile(null);
		setPreview(user?.logo);
	};

	const onSelectFile = (e) => {
		if (!e.target.files || e.target.files.length === 0) {
			setSelectedFile(null);
			return;
		}
		const file = e.target.files[0];
		if (!isImageFile(file)) {
			dispatch(Actions.showMessage({ message: 'Please select an image file', variant: 'error' }));
		} else {
			setSelectedFile(e.target.files[0]);
		}
	};

	useEffect(() => {
		if (!selectedFile) {
			return;
		}

		const objectUrl = URL.createObjectURL(selectedFile);
		setPreview(objectUrl);

		return () => URL.revokeObjectURL(objectUrl);
	}, [selectedFile]);

	const onSubmit = async (data) => {
		setIsSaving(true);
		try {
			const payload = {
				...data,
				school_id: mainUser.school.id,
				timezone: cityTimezone || data.timezone,
				timezone_checkin: cityTimezone || data.timezone_checkin,
			};

			if (selectedFile) {
				const filename = getImageUrl(selectedFile);
				const uploadResponse = await uploadFile(selectedFile, filename);
				payload.school_profile_image = `${import.meta.env.VITE_S3_BASE_URL}${uploadResponse}`;
			}

			const response = await updatesettings(payload);
			dispatch(
				Actions.showMessage({
					message: response.data.message,
					autoHideDuration: 1500,
					variant: 'success',
				})
			);
			setIsEditing(false);
			GetSchoolSettings();
		} catch (error) {
			if (error.response?.data?.errors) {
				Object.entries(error.response.data.errors).forEach(([key, value]) => {
					setError(key, { type: 'manual', message: value });
				});
			} else {
				dispatch(
					Actions.showMessage({
						message: 'Failed to update settings.',
						autoHideDuration: 1500,
						variant: 'error',
					})
				);
			}
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<div className="mx-auto p-4 !mb-[162px]">
			<div className="m-32 mt-24">
				<div className="flex items-center gap-4 md:gap-0 justify-between mb-32">
					<PageHeader title="School Settings" hasBack={false} />
					<div className="personal-button">
						{!isEditing ? (
							<GradientButton
								onClick={() => {
									setIsEditing(true);
								}}
								variant="blurple"
								size="small"
							>
								Edit
							</GradientButton>
						) : (
							<div />
						)}
					</div>
				</div>

				<form onSubmit={handleSubmit(onSubmit)} className="bg-white p-32 rounded md:py-64">
					<div className="flex flex-col md:flex-row gap-32">
						<div className="relative">
							{!isEditing ? (
								<Avatar src={preview} className="size-128 object-cover" />
							) : (
								<div
									className="cursor-pointer relative group"
									onClick={() => inputRef.current.click()}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											inputRef.current.click();
										}
									}}
									role="button"
									tabIndex={0}
								>
									<Avatar src={preview} className="size-128 object-cover" />
									<div className="upload-button absolute inset-0 opacity-0 group-hover:opacity-50 transition-opacity duration-300 flex items-center justify-center">
										<i className="fa fa-2x fa-camera text-black" />
									</div>
									<input
										accept="image/*"
										className="hidden"
										id="image"
										name="file"
										onChange={onSelectFile}
										ref={inputRef}
										type="file"
									/>
								</div>
							)}
						</div>

						<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-24 gap-y-48 w-full mt-32 md:px-32">
							<Controller
								control={control}
								name="school_name"
								rules={{
									required: 'School name is required',
									pattern: {
										value: /^[a-zA-Z ]+$/,
										message: 'Please enter a valid value',
									},
								}}
								render={({ field }) => (
									<StyledInput
										{...field}
										disabled={!isEditing}
										error={!!errors.school_name}
										helperText={errors?.school_name?.message}
										label="School Name"
										required={isEditing}
										variant={!isEditing ? 'standard' : 'outlined'}
										isInformational={!isEditing}
									/>
								)}
							/>
							<Controller
								control={control}
								name="school_phone"
								rules={{
									required: 'Phone number is required',
									validate: (value) => isPossiblePhoneNumber(value, 'US') || 'Please enter valid phone number',
								}}
								render={({ field }) => (
									<StyledInput
										{...field}
										disabled={!isEditing}
										error={!!errors.school_phone}
										type="phone"
										helperText={errors?.school_phone?.message}
										label="School Phone Number"
										required={isEditing}
										variant={!isEditing ? 'standard' : 'outlined'}
										isInformational={!isEditing}
									/>
								)}
							/>
							<Controller
								control={control}
								name="address"
								rules={{
									required: 'Address is required',
								}}
								render={({ field }) => (
									<StyledInput
										{...field}
										disabled={!isEditing}
										error={!!errors.address}
										helperText={errors?.address?.message}
										label="Address 1"
										required={isEditing}
										variant={!isEditing ? 'standard' : 'outlined'}
										isInformational={!isEditing}
									/>
								)}
							/>
							<Controller
								control={control}
								name="street"
								render={({ field }) => (
									<StyledInput
										{...field}
										disabled={!isEditing}
										error={!!errors.street}
										helperText={errors?.street?.message}
										label="Address 2"
										variant={!isEditing ? 'standard' : 'outlined'}
										isInformational={!isEditing}
									/>
								)}
							/>
							<Controller
								control={control}
								name="website"
								rules={{
									pattern: {
										value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
										message: 'Please enter a valid website URL',
									},
								}}
								render={({ field }) => (
									<StyledInput
										{...field}
										disabled={!isEditing}
										error={!!errors.website}
										helperText={errors?.website?.message}
										label="School Website"
										variant={!isEditing ? 'standard' : 'outlined'}
										isInformational={!isEditing}
										placeholder="https://www.example.com"
									/>
								)}
							/>

							<StyledInput
								disabled
								label="School Code"
								value={user.otp}
								fullWidth
								variant={!isEditing ? 'standard' : 'outlined'}
								isInformational={!isEditing}
							/>

							<StyledInput
								disabled
								value="United States"
								label="Country"
								variant={!isEditing ? 'standard' : 'outlined'}
								isInformational={!isEditing}
							/>
							<Controller
								control={control}
								name="state_id"
								rules={{
									required: 'State is required',
								}}
								render={({ field }) => (
									<StyledSelect
										{...field}
										disabled={!isEditing}
										error={!!errors.state_id}
										helperText={errors?.state_id?.message}
										label="State"
										variant={!isEditing ? 'standard' : 'outlined'}
										labelClasses="text-black/38"
										isInformational={!isEditing}
										options={states}
										required={isEditing}
										fullWidth
									/>
								)}
							/>
							<Controller
								control={control}
								name="city"
								rules={{
									required: 'City is required',
								}}
								render={({ field }) => (
									<StyledSearchSelect
										disabled={!isEditing}
										error={!!errors.city}
										helperText={errors?.city?.message}
										isLoading={citiesLoading}
										label="City"
										options={cities.map((c) => ({
											label: c.name,
											value: c.name,
											meta: c.meta,
										}))}
										placeholder="Select City"
										required={isEditing}
										variant={!isEditing ? 'standard' : 'outlined'}
										labelClasses="text-black/38"
										isInformational={!isEditing}
										resultsLoading={citiesLoading}
										widthClass="w-auto justify-end"
										setControlledQuery={setSearchCityQuery}
										value={
											defaultCity
												? {
														label: defaultCity.name,
														value: defaultCity.name,
														meta: defaultCity.meta,
													}
												: null
										}
										onChange={(selected) => {
											field.onChange(selected?.value);
											setDefaultCity({
												name: selected?.value,
												meta: selected?.meta,
											});
											if (selected?.meta) {
												const value = JSON.parse(selected.meta);
												setLatLong({
													lat: value.latitude,
													long: value.longitude,
												});
											}
										}}
									/>
								)}
							/>
							<Controller
								control={control}
								name="zip_code"
								rules={{
									required: 'Zip code is required',
									pattern: {
										value: /^\d{5}$/,
										message: 'Please enter a valid 5-digit zip code',
									},
								}}
								render={({ field }) => (
									<StyledInput
										{...field}
										disabled={!isEditing}
										error={!!errors.zip_code}
										helperText={errors?.zip_code?.message}
										label="Zipcode"
										required={isEditing}
										variant={!isEditing ? 'standard' : 'outlined'}
										isInformational={!isEditing}
									/>
								)}
							/>
							<Controller
								control={control}
								name="status"
								render={({ field }) => (
									<StyledSelect
										{...field}
										disabled={!isEditing}
										label="Status"
										labelClasses="text-black/38"
										variant={!isEditing ? 'standard' : 'outlined'}
										isInformational={!isEditing}
										options={[
											{ label: 'Active', value: 1 },
											{ label: 'Inactive', value: 0 },
										]}
									/>
								)}
							/>
							<Controller
								control={control}
								name="checkout_time"
								render={({ field }) => (
									<StyledSelect
										{...field}
										disabled={!isEditing}
										label="Auto Check-out Time"
										labelClasses="text-black/38"
										variant={!isEditing ? 'standard' : 'outlined'}
										isInformational={!isEditing}
										options={checkoutTimes}
									/>
								)}
							/>
						</div>
					</div>

					{isEditing ? (
						<div className="flex justify-center md:justify-end mt-32">
							{!isSaving ? (
								<div className="flex gap-16">
									<GradientButton onClick={handleCancel} variant="secondary" size="small">
										Cancel
									</GradientButton>
									<GradientButton type="submit" variant="blurple" size="small">
										Update
									</GradientButton>
								</div>
							) : (
								<CircularProgress />
							)}
						</div>
					) : null}
				</form>
			</div>
		</div>
	);
};

export default Settings;
